import { useCallback, useEffect, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { providerCatalogApi } from "@services/providerCatalogApi";
import { adminMonitoringApi } from "@services/adminMonitoringApi";
import type { ProviderAdapter } from "@models/providerCatalog";

dayjs.extend(utc);

/** A provider a flow step can use: the registry key it is stored by, and its name. */
export interface CatalogProvider {
  key: string;
  label: string;
}

export type ModelCapability = "chat" | "ocr";

/** How far back model suggestions look for models actually used. */
const SUGGESTION_DAYS = 90;

let catalogPromise: Promise<ProviderAdapter[]> | null = null;
const suggestionCache = new Map<string, Promise<string[]>>();

/** The runtime's supported providers that serve ``capability`` (cached per session). */
const loadCatalog = async (capability: ModelCapability): Promise<CatalogProvider[]> => {
  catalogPromise ??= providerCatalogApi().catalog().then((catalog) => catalog.providers);
  try {
    const adapters = await catalogPromise;
    return adapters
      .filter((p) => p.supported && p.capabilities.includes(capability))
      .map(({ key, label }) => ({ key, label }));
  } catch (error) {
    catalogPromise = null;
    throw error;
  }
};

/**
 * Suggestions for a provider's model field: models it was actually called with
 * recently (from metering) first, then the ones the model catalog prices or retires. Only
 * suggestions: the field stays free text, so an id in neither list (an Azure
 * deployment name) is still valid. A source the caller may not read is skipped.
 */
const loadSuggestions = (providerKey: string, capability: ModelCapability): Promise<string[]> => {
  const cacheKey = `${providerKey}|${capability}`;
  if (!suggestionCache.has(cacheKey)) {
    const to = dayjs().utc().startOf("day").add(1, "day");
    const used = adminMonitoringApi()
      .facets({ from: to.subtract(SUGGESTION_DAYS, "day").toISOString(), to: to.toISOString(), provider: providerKey,
                capability })
      .then((facets) => facets.models.map((m) => m.value).filter((m) => m && m !== "unknown"))
      .catch(() => [] as string[]);
    const known = providerCatalogApi()
      .models({ provider: providerKey, capability })
      .then((catalog) => catalog.models.map((m) => m.model_id))
      .catch(() => [] as string[]);
    suggestionCache.set(cacheKey, Promise.all([used, known]).then(([a, b]) => [...new Set([...a, ...b])]));
  }
  return suggestionCache.get(cacheKey)!;
};

/**
 * Providers (from core's runtime catalog) and model suggestions for flow steps.
 * Replaces the retired provider/model CRUD catalog: a step stores a provider key
 * and a model name, nothing else.
 */
export const useModelCatalog = (capability: ModelCapability, enabled = true) => {
  const [providers, setProviders] = useState<CatalogProvider[]>([]);
  const [isLoadingProviders, setIsLoadingProviders] = useState(false);
  const [suggestions, setSuggestions] = useState<Record<string, string[]>>({});
  const [loadingModelsFor, setLoadingModelsFor] = useState<string | null>(null);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    setIsLoadingProviders(true);
    loadCatalog(capability)
      .then((list) => alive && setProviders(list))
      .catch(() => alive && setProviders([]))
      .finally(() => alive && setIsLoadingProviders(false));
    return () => {
      alive = false;
    };
  }, [capability, enabled]);

  const loadModels = useCallback(async (providerKey: string) => {
    if (!providerKey) return;
    setLoadingModelsFor(providerKey);
    const models = await loadSuggestions(providerKey, capability);
    setSuggestions((previous) => ({ ...previous, [providerKey]: models }));
    setLoadingModelsFor((current) => (current === providerKey ? null : current));
  }, [capability]);

  return {
    providers,
    isLoadingProviders,
    /** Suggestions loaded so far, by provider key. */
    suggestions,
    modelsFor: (providerKey: string) => suggestions[providerKey] ?? [],
    isLoadingModels: loadingModelsFor !== null,
    loadModels,
  };
};
